import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, MessageCircle, X, Clock, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Floating WhatsApp button with contact options
 */
export const WhatsAppButton = ({ className }) => {
  const [isOpen, setIsOpen] = useState(false);

  const contacts = [
    {
      id: 'main',
      label: 'Servicio Técnico',
      phone: '624473293',
      description: 'Consultas y seguimiento',
      icon: MessageCircle,
      color: 'bg-[#25D366]'
    },
    {
      id: 'emergency',
      label: 'Emergencias',
      phone: '642676910',
      description: 'Urgencias 24h',
      icon: AlertTriangle,
      color: 'bg-[#EF4444]'
    }
  ];

  const openWhatsApp = (phone, message = '') => {
    const url = `https://wa.me/34${phone}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
    window.open(url, '_blank');
  };

  return (
    <div className={cn('fixed bottom-24 left-4 z-[9998]', className)}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="absolute bottom-16 left-0 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-[#E5E7EB] dark:border-gray-700 overflow-hidden w-72"
          >
            {/* Header */}
            <div className="bg-[#25D366] text-white p-4">
              <h3 className="font-semibold">Contacta con nosotros</h3>
              <p className="text-xs text-white/80">Respuesta rápida por WhatsApp</p>
            </div>

            {/* Options */}
            <div className="p-2">
              {contacts.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => openWhatsApp(contact.phone, 'Hola, necesito ayuda con mi garantía')}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-[#F5F5F7] dark:hover:bg-gray-700 transition-colors text-left"
                >
                  <div className={`w-10 h-10 ${contact.color} rounded-full flex items-center justify-center`}>
                    <contact.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-sm dark:text-white">{contact.label}</p>
                    <p className="text-xs text-[#52525B] dark:text-gray-400">{contact.description}</p>
                  </div>
                </button>
              ))}

              {/* Phone call option */}
              <a
                href="tel:+34624473293"
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-[#F5F5F7] dark:hover:bg-gray-700 transition-colors"
              >
                <div className="w-10 h-10 bg-[#0047FF] rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-sm dark:text-white">Llamar ahora</p>
                  <p className="text-xs text-[#52525B] dark:text-gray-400">624 47 32 93</p>
                </div>
              </a>
            </div>

            {/* Hours */}
            <div className="px-4 pb-4">
              <div className="flex items-center gap-2 text-xs text-[#52525B] dark:text-gray-400 bg-[#F5F5F7] dark:bg-gray-700 p-2 rounded-lg">
                <Clock className="w-3 h-3" />
                <span>L-V: 9:00-18:00 | Sáb: 10:00-14:00</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-colors',
          isOpen ? 'bg-[#52525B]' : 'bg-[#25D366] hover:bg-[#20BD5A]'
        )}
        aria-label={isOpen ? 'Cerrar contacto' : 'Abrir WhatsApp'}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        )}
      </motion.button>

      {/* Pulse animation when closed */}
      {!isOpen && (
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-25" />
      )}
    </div>
  );
};

/**
 * Contact header widget
 */
export const ContactHeader = ({ className }) => {
  return (
    <div className={cn('flex items-center gap-4 text-sm', className)}>
      <a
        href="tel:+34624473293"
        className="flex items-center gap-1 text-[#52525B] dark:text-gray-400 hover:text-[#0047FF] transition-colors"
      >
        <Phone className="w-4 h-4" />
        <span className="hidden sm:inline">624 47 32 93</span>
      </a>
      <a
        href="https://wa.me/34624473293"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-[#25D366] hover:text-[#20BD5A] transition-colors"
      >
        <MessageCircle className="w-4 h-4" />
        <span className="hidden sm:inline">WhatsApp</span>
      </a>
    </div>
  );
};

export default WhatsAppButton;
